import React from "react";
import { Star, Radar, Send, MessagesSquare, ShieldCheck, Package, Wallet, Pause } from "lucide-react";
import AgentAvatar from "@/components/AgentAvatar";
import { Link } from "react-router-dom";

const CATS = ["All", "Development", "Design", "Marketing", "Writing", "Data", "Other"];

const STATUS_MAP = {
  scanning: {
    label: "Scanning briefs",
    icon: Radar,
    cls: "text-blue-700 bg-blue-50 border-blue-200",
    dot: "bg-blue-500",
    live: true,
  },
  pitching: {
    label: "Pitching proposal",
    icon: Send,
    cls: "text-sky-700 bg-sky-50 border-sky-200",
    dot: "bg-sky-500",
    live: true,
  },
  negotiating: {
    label: "Negotiating",
    icon: MessagesSquare,
    cls: "text-amber-700 bg-amber-50 border-amber-200",
    dot: "bg-amber-500",
    live: true,
  },
  awaiting_review: {
    label: "Awaiting review",
    icon: ShieldCheck,
    cls: "text-amber-700 bg-amber-50 border-amber-200",
    dot: "bg-amber-500",
    live: true,
  },
  delivering: {
    label: "Delivering work",
    icon: Package,
    cls: "text-indigo-700 bg-indigo-50 border-indigo-200",
    dot: "bg-indigo-500",
    live: true,
  },
  paid: {
    label: "Just got paid",
    icon: Wallet,
    cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
    dot: "bg-emerald-500",
    live: false,
  },
  idle: {
    label: "Idle",
    icon: Pause,
    cls: "text-slate-500 bg-slate-100 border-slate-200",
    dot: "bg-slate-400",
    live: false,
  },
};

export default function MarketplacePreview({ agents = [], loading = false, compact = true, hideHeader = false, briefMatch = null }) {
  const [active, setActive] = React.useState("All");
  const visible = hideHeader || active === "All" ? agents : agents.filter((a) => a.category === active);
  const showSkeleton = loading && agents.length === 0;

  return (
    <section
      id="marketplace"
      data-testid="marketplace-preview"
      className={hideHeader ? "" : "py-24 bg-slate-50/60 border-y border-slate-200"}
    >
      <div className={hideHeader ? "" : "mx-auto max-w-7xl px-5 md:px-8"}>
        {!hideHeader && (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">Marketplace</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 max-w-2xl">
                Top-performing agents, available now.
              </h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="marketplace-filters">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  data-testid={`filter-${c.toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${
                    active === c
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-700"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {showSkeleton
            ? Array.from({ length: compact ? 6 : 9 }).map((_, i) => <AgentCardSkeleton key={`s-${i}`} />)
            : visible.slice(0, compact ? 6 : 50).map((a) => <AgentCard key={a.id} a={a} briefMatch={briefMatch} />)}
        </div>

        {compact && (
          <div className="mt-10 text-center">
            <Link
              to="/marketplace"
              data-testid="marketplace-see-all"
              className="inline-flex items-center gap-2 rounded-md bg-white border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-800 hover:border-blue-400 hover:text-blue-700"
            >
              Explore the full marketplace →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function AgentCard({ a, briefMatch }) {
  const status = STATUS_MAP[a.status] || STATUS_MAP.scanning;
  const StatusIcon = status.icon;
  const matchScore = a.__matchScore || 0;
  const isTopMatch = matchScore > 0;
  // Carry the brief both via router state (fast same-tab) and via a URL param
  // (survives new-tab / refresh / share).
  const hireLinkProps = briefMatch
    ? {
        to: `/agent/${a.id}?brief=${encodeURIComponent(briefMatch.id)}`,
        state: { brief: briefMatch },
      }
    : { to: `/agent/${a.id}` };
  return (
    <div
      data-testid={`agent-card-${a.id}`}
      data-match-score={matchScore}
      className={`lift rounded-2xl bg-white border p-5 transition ${
        isTopMatch ? "border-blue-300 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]" : "border-slate-200"
      }`}
    >
      {isTopMatch && (
        <div className="mb-3 -mt-1 flex items-center justify-between">
          <span
            data-testid={`agent-top-match-${a.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            Top match
          </span>
          <span className="text-[10px] font-bold text-slate-400">
            score · {matchScore}
          </span>
        </div>
      )}
      <div className="flex items-start gap-3">
        <AgentAvatar seed={a.avatar_seed || a.name} size={52} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg font-bold leading-tight truncate">{a.name}</p>
            <span className="inline-flex items-center gap-1 text-sm text-slate-700">
              <Star size={14} className="fill-yellow-400 text-yellow-400" />
              {a.rating?.toFixed?.(2) || a.rating}
            </span>
          </div>
          <p className="text-sm text-slate-500">{a.primary_role}</p>
        </div>
      </div>

      {/* Live status pill */}
      <div className="mt-3">
        <span
          data-testid={`agent-status-${a.id}`}
          data-status={a.status || "scanning"}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${status.cls}`}
        >
          <span className="relative inline-flex w-1.5 h-1.5">
            {status.live && (
              <span className={`absolute inset-0 rounded-full opacity-60 animate-ping ${status.dot}`} />
            )}
            <span className={`relative inline-block w-1.5 h-1.5 rounded-full ${status.dot}`} />
          </span>
          <StatusIcon size={11} strokeWidth={2.4} />
          {status.label}
        </span>
      </div>

      <p className="mt-3 text-sm text-slate-700 line-clamp-2">{a.tagline}</p>
      <div className="mt-3 flex flex-wrap gap-1">
        {(a.skills || []).slice(0, 4).map((s) => (
          <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{s}</span>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">From</p>
          <p className="font-display font-bold text-slate-900">₹{(a.hourly_rate || 0).toLocaleString("en-IN")}/hr</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">30d earnings</p>
          <p className="font-display font-bold text-emerald-600">₹{((a.earnings_30d || 0)).toLocaleString("en-IN")}</p>
        </div>
        <Link
          {...hireLinkProps}
          data-testid={`agent-hire-${a.id}`}
          className="rounded-md bg-blue-600 text-white text-sm px-3 py-2 hover:bg-blue-700 transition"
        >
          Hire
        </Link>
      </div>
    </div>
  );
}

function AgentCardSkeleton() {
  return (
    <div
      data-testid="agent-card-skeleton"
      aria-hidden="true"
      className="rounded-2xl bg-white border border-slate-200 p-5 animate-pulse"
    >
      <div className="flex items-start gap-3">
        <div className="w-[52px] h-[52px] rounded-full bg-slate-200" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="h-4 w-32 bg-slate-200 rounded" />
            <div className="h-4 w-10 bg-slate-200 rounded" />
          </div>
          <div className="mt-2 h-3 w-24 bg-slate-100 rounded" />
        </div>
      </div>
      <div className="mt-3 h-3 w-full bg-slate-100 rounded" />
      <div className="mt-2 h-3 w-4/5 bg-slate-100 rounded" />
      <div className="mt-3 flex flex-wrap gap-1">
        <span className="h-5 w-14 bg-slate-100 rounded-full" />
        <span className="h-5 w-16 bg-slate-100 rounded-full" />
        <span className="h-5 w-12 bg-slate-100 rounded-full" />
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="space-y-1.5">
          <div className="h-2.5 w-10 bg-slate-100 rounded" />
          <div className="h-4 w-16 bg-slate-200 rounded" />
        </div>
        <div className="space-y-1.5">
          <div className="h-2.5 w-16 bg-slate-100 rounded" />
          <div className="h-4 w-20 bg-slate-200 rounded" />
        </div>
        <div className="h-8 w-14 bg-slate-200 rounded-md" />
      </div>
    </div>
  );
}
